import React, { useEffect } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  FormHelperText,
  OutlinedInput,
  Typography
} from '@mui/material';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { black, gray, primarycolor } from '../../constants/Colors';
import { textMb } from '../../constants/Location';

import type { ClientRegisterCompanyTypeWhenAgent } from './AgentClientRegisterCompanyCard';
import type { ClientRegisterCompanyType } from './RegisterCompanyCard';
import type { SxProps } from '@mui/material';
import type {
  Control,
  FieldError,
  FieldErrorsImpl,
  Merge
} from 'react-hook-form';

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  error?: boolean;
  elms?: Array<{ productUrls: string }>;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<ClientRegisterCompanyType['products']>>
    | undefined;
  helperTextUrl?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  helperTextsElm?:
    | Merge<
        FieldError,
        Array<
          | Merge<
              FieldError,
              FieldErrorsImpl<{
                productNames: string;
                productUrls: string;
              }>
            >
          | undefined
        >
      >
    | undefined;

  width?: string;
  control: Control<
    ClientRegisterCompanyType | ClientRegisterCompanyTypeWhenAgent
  >;
};

export default function FocusProduct(props: Props): React.ReactElement {
  const { t } = useTranslation();

  const {
    error,

    helperTextsElm,
    control,
    elms,
    ...rest
  } = props;

  const { fields, append } = useFieldArray({
    control,
    name: 'products'
  });

  function inputBorderColor(): SxProps | undefined {
    if (error) {
      return {
        '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: primarycolor
        }
      };
    }
  }

  const styles = {
    addbutton: {
      height: '36px',
      width: '98px'
    },
    helper: {
      '@media screen and (max-width:480px)': {
        marginLeft: 0
      }
    },
    label: {
      fontSize: '12px'
    },
    list: {
      display: 'flex',
      marginBottom: '8px'
    },
    productNameInput: {
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: black
      },
      '@media screen and (max-width:480px)': {
        width: '311px'
      },
      height: '40px',
      inputBorderColor,
      marginRight: '8px',
      width: '400px'
    },
    productUrlInput: {
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: black
      },
      '@media screen and (max-width:480px)': {
        width: '311px'
      },
      height: '40px',
      inputBorderColor,
      width: '400px'
    }
  };

  const textfieldUrlHelper = (i: number): string | undefined => {
    if (helperTextsElm !== undefined) {
      if (helperTextsElm[i]?.productUrls?.message === undefined) return;
      return String(helperTextsElm[i]?.productUrls?.message ?? '');
    }
  };

  useEffect(() => {
    if (elms) {
      elms.map((e) => append({ productNames: '', productUrls: e.productUrls }));
    }
  }, [elms]);

  return (
    <>
      <Typography sx={textMb} align="center" component="div">
        <Typography align="left" component="div">
          <Typography color={gray} sx={styles.label} component="span">
            {t('form.focusURL')}
          </Typography>
        </Typography>
        {fields.map((elem, index) => (
          <Typography component="div" sx={styles.list} key={elem.id}>
            <Controller
              render={({ field }) => (
                <Typography component="div">
                  <OutlinedInput
                    {...field}
                    {...rest}
                    sx={styles.productUrlInput}
                    placeholder=" "
                    type="url"
                  />
                  <FormHelperText sx={styles.helper} error={error}>
                    {textfieldUrlHelper(index)}
                  </FormHelperText>
                </Typography>
              )}
              control={control}
              name={`products.${index}.productUrls`}
            />
          </Typography>
        ))}
        <Button
          variant="text"
          onClick={() => {
            append({ productNames: '', productUrls: '' });
          }}
          sx={[styles.addbutton, fields.length > 9 && { display: 'none' }]}
        >
          <AddIcon sx={{ height: '20px', width: '20px' }} />
          <Typography sx={{ fontSize: '14px' }}>{t('form.add')}</Typography>
        </Button>
      </Typography>
    </>
  );
}
